// Cronograma de pagos: los depósitos de los últimos 14 días y lo que viene, proyectado.
// Lo que viene sale del saldo (lo exigible y lo retenido para el día 1) y de los cierres pendientes
// que calcula el motor. Un depósito junta todo lo exigible y no dice de qué regla viene: se pinta
// por su estado.
import type { RetailCommission } from '@fiado/type-kit';
import { weekLabel } from '@/domains/retail/admin/comisiones';
import { dayOffset, formatShortDayMx, mxDay } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { paymentStatusOf, type BalanceView, type PaymentStatusView } from './myPaymentsView';

type Mine = RetailCommission.GetMyCommissionsResponse;
type Payment = RetailCommission.CommissionPayment;
type UpcomingCut = RetailCommission.MyCommissionsUpcomingCut;

/** Días de depósitos que muestra el cronograma, hoy incluido. */
export const SCHEDULE_DAYS = 14;

/** Un depósito real, o lo que se espera: lo exigible, el cierre de la semana o el del mes. */
export type ScheduleLane = 'deposit' | 'payable' | 'cut' | 'monthly';

export interface ScheduleRow {
    key: string;
    lane: ScheduleLane;
    /** Día en el piso de venta, `YYYY-MM-DD`. */
    day: string;
    /** Instante del depósito; `null` en lo proyectado. */
    at: string | null;
    title: string;
    detail: string;
    amountCents: number;
    /** Estado del depósito; `null` en lo proyectado. */
    status: PaymentStatusView | null;
}

export interface PaymentSchedule {
    /** Lo que viene, del más lejano al más cercano. */
    upcoming: ScheduleRow[];
    /** Depósitos de los últimos 14 días, del más reciente al más viejo. */
    recent: ScheduleRow[];
    /** Depósitos anteriores a esa ventana, del más reciente al más viejo. */
    older: ScheduleRow[];
}

/** El día 1 del mes siguiente. */
export function firstOfNextMonth(day: string): string {
    const [year, month] = day.split('-').map(Number);
    return new Date(Date.UTC(year, month, 1, 12)).toISOString().slice(0, 10);
}

/** Cuándo se libera lo retenido: el día 1 del mes siguiente; si hoy es día 1 y sigue retenido, hoy. */
export function retainedReleaseDay(today: string, retainedCents: number): string {
    return today.endsWith('-01') && retainedCents > 0 ? today : firstOfNextMonth(today);
}

/** Los cierres pendientes que manda el motor; vacío si no los manda. */
export const upcomingCutsOf = (mine: Mine): readonly UpcomingCut[] => mine.upcomingCuts ?? [];

/** El próximo cierre que deja algo por depositar (o por descontar). */
export function nextCutOf(mine: Mine): UpcomingCut | null {
    return upcomingCutsOf(mine).find((cut) => cut.payableCents !== 0) ?? null;
}

/** La tarjeta de lo pendiente: todo lo ganado que todavía no cae en la wallet. */
export interface PendingCard {
    totalCents: number;
    /** Lo exigible de cortes cerrados. */
    closedCents: number;
    inTransitCents: number;
    /** Los cierres abiertos que suman. */
    openCuts: UpcomingCut[];
    /** Los que descuentan: van aparte y no restan de la cifra. */
    discounts: UpcomingCut[];
}

export function pendingCardOf(mine: Mine, balance: BalanceView): PendingCard {
    const cuts = upcomingCutsOf(mine);
    const openCuts = cuts.filter((cut) => cut.payableCents > 0);
    const openCents = openCuts.reduce((sum, cut) => sum + cut.payableCents, 0);
    return {
        totalCents: balance.payableCents + balance.inTransitCents + openCents,
        closedCents: balance.payableCents,
        inTransitCents: balance.inTransitCents,
        openCuts,
        discounts: cuts.filter((cut) => cut.payableCents < 0),
    };
}

/** La tarjeta mensual: lo retenido de cortes cerrados y lo que retienen los abiertos. */
export interface MonthlyCard {
    totalCents: number;
    closedCents: number;
    openCents: number;
    /** Cuánto se libera cada día 1, del más cercano al más lejano. */
    days: Array<{ day: string; cents: number }>;
}

export function monthlyCardOf(mine: Mine, balance: BalanceView, today: string): MonthlyCard {
    const byDay = new Map<string, number>();
    const add = (day: string, cents: number) => byDay.set(day, (byDay.get(day) ?? 0) + cents);
    if (balance.retainedCents > 0) {
        add(retainedReleaseDay(today, balance.retainedCents), balance.retainedCents);
    }
    let openCents = 0;
    // Sin fecha de liberación no se proyecta, igual que en el cronograma.
    for (const cut of upcomingCutsOf(mine)) {
        if (cut.retainedCents > 0 && cut.releaseDate) {
            add(cut.releaseDate, cut.retainedCents);
            openCents += cut.retainedCents;
        }
    }
    return {
        totalCents: balance.retainedCents + openCents,
        closedCents: balance.retainedCents,
        openCents,
        days: [...byDay]
            .map(([day, cents]) => ({ day, cents }))
            .sort((a, b) => a.day.localeCompare(b.day)),
    };
}

/** El detalle de la fila del cierre: de dónde sale y qué falta. */
export function cutDetailOf(cut: UpcomingCut, debtCents: number): string {
    const parts = ['Proyectado con lo que llevas'];
    if (cut.adjustmentsCents !== 0) {
        parts.push(`incluye ${formatMoneyCents(cut.adjustmentsCents)} de ajustes`);
    }
    if (debtCents > 0)
        parts.push(`primero se descuenta lo que debes (${formatMoneyCents(debtCents)})`);
    else if (cut.payableCents < 0) parts.push('se descuenta al cerrar');
    else parts.push('se fija al cerrar');
    return parts.join(' · ');
}

/** Cómo se llama el cierre: el próximo, o "en proceso" si ya debió cerrar. */
export function cutHeadOf(cut: UpcomingCut, today: string): string {
    return cut.cutDate < today
        ? `Cierre del ${formatShortDayMx(cut.cutDate)} (en proceso)`
        : `Próximo cierre (${formatShortDayMx(cut.cutDate)})`;
}

/** La línea del cierre cuando hay deuda, en tres partes para resaltar el monto. */
export interface DebtCutText {
    lead: string;
    amount: string;
    tail: string;
}

/** El efecto neto del cierre sobre la deuda: lo que genera se aplica primero a ella. */
export function debtCutTextOf(cut: UpcomingCut, debtCents: number, today: string): DebtCutText {
    const head = cutHeadOf(cut, today);
    const gain = cut.payableCents;
    if (gain < 0) {
        return {
            lead: `${head}: se descuentan `,
            amount: formatMoneyCents(-gain),
            tail: ' más',
        };
    }
    const lead = `${head}: `;
    const amount = `+${formatMoneyCents(gain)}`;
    if (debtCents > gain) {
        return {
            lead,
            amount,
            tail: ` se aplican a tu deuda; quedarías debiendo ${formatMoneyCents(debtCents - gain)}`,
        };
    }
    const left = gain - debtCents;
    return {
        lead,
        amount,
        tail:
            left > 0
                ? ` cubren tu deuda; te quedarían ${formatMoneyCents(left)} por depositar`
                : ' cubren tu deuda',
    };
}

function depositRow(payment: Payment): ScheduleRow {
    const status = paymentStatusOf(payment);
    return {
        key: payment.paymentId,
        lane: 'deposit',
        day: mxDay(payment.requestedAt),
        at: payment.requestedAt,
        title: 'Depósito a tu wallet',
        detail: status.note ?? payment.concept,
        amountCents: payment.amountCents,
        status,
    };
}

/** Los depósitos de los últimos 14 días, lo que viene y los depósitos anteriores. */
export function paymentScheduleOf(
    mine: Mine,
    balance: BalanceView,
    payments: readonly Payment[],
    today: string,
): PaymentSchedule {
    const upcoming: ScheduleRow[] = [];
    const projected = (row: Omit<ScheduleRow, 'at' | 'status'>) => {
        upcoming.push({ ...row, at: null, status: null });
    };

    for (const cut of upcomingCutsOf(mine)) {
        if (cut.payableCents !== 0) {
            projected({
                key: `cut-${cut.periodId}`,
                lane: 'cut',
                // Un cierre que ya debió terminar queda en hoy, arriba de la línea.
                day: cut.cutDate > today ? cut.cutDate : today,
                title: `Cierre de semana · ${weekLabel(cut.periodId)}`,
                detail: cutDetailOf(cut, balance.debtCents),
                amountCents: cut.payableCents,
            });
        }
        if (cut.retainedCents > 0 && cut.releaseDate) {
            projected({
                key: `month-${cut.periodId}`,
                lane: 'monthly',
                day: cut.releaseDate,
                title: 'Cierre de mes',
                detail: `Parte mensual del corte ${weekLabel(cut.periodId)}, proyectada`,
                amountCents: cut.retainedCents,
            });
        }
    }
    if (balance.retainedCents > 0) {
        projected({
            key: 'retained',
            lane: 'monthly',
            day: retainedReleaseDay(today, balance.retainedCents),
            title: 'Cierre de mes',
            detail: 'Retenido de cortes cerrados',
            amountCents: balance.retainedCents,
        });
    }
    if (balance.payableCents > 0) {
        projected({
            key: 'payable',
            lane: 'payable',
            day: today,
            title: 'Por depositar',
            detail: balance.reason ?? 'Entra en tu siguiente depósito',
            amountCents: balance.payableCents,
        });
    }
    upcoming.sort((a, b) => b.day.localeCompare(a.day));

    const from = dayOffset(today, -(SCHEDULE_DAYS - 1));
    const deposits = [...payments]
        .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))
        .map(depositRow);
    return {
        upcoming,
        recent: deposits.filter((row) => row.day >= from),
        older: deposits.filter((row) => row.day < from),
    };
}
